import type {
  MicrositeConfigurationDocument,
  MicrositeSource
} from '@cdf/api-client-react';
import { createDefaultLayout, MICROSITE_SCHEMA_VERSION, MICROSITE_VISUAL_BRAND_DEFAULTS } from '@cdf/microsite-layout';

import { getMicrositeContrastRatio } from './microsite-colors';
import { normalizeMicrositeEmbedUrl } from './microsite-embed';

export type MicrositeEditorValue = {
  slug: string;
  canonicalHost: string;
  configuration: MicrositeConfigurationDocument;
};

export function publicMicrositeUrl(
  { slug, canonicalHost }: { slug: string; canonicalHost?: string | null },
  origin = window.location.origin
): string {
  return `${canonicalHost ? `https://${canonicalHost}` : origin}/sites/${encodeURIComponent(slug)}`;
}

const slugify = (value: string) => value.toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 80);

export function createMicrositeEditorValue(entityName: string): MicrositeEditorValue {
  const slug = slugify(entityName);
  const enquiryCallToAction = {
    heading: 'How can we help?',
    label: 'Send an enquiry',
    supportingText: 'Tell us what you need and our team will respond.'
  };
  return {
    slug: slug.length >= 3 ? slug : `site-${slug || 'entity'}`,
    canonicalHost: '',
    configuration: {
      schemaVersion: MICROSITE_SCHEMA_VERSION,
      headline: entityName,
      description: `Discover how ${entityName} can help.`,
      publicWebsiteUrl: 'https://example.com',
      brand: {
        logoAssetId: null,
        heroAssetId: null,
        primaryColor: '#111111',
        backgroundColor: '#ffffff',
        foregroundColor: '#111111',
        ...MICROSITE_VISUAL_BRAND_DEFAULTS
      },
      startingInstruction: 'Create a concise overview using only the approved information.',
      promptChoices: [{
        id: crypto.randomUUID(),
        label: 'Our services',
        instruction: 'Explain the approved services and customer outcomes.'
      }],
      approvedContent: [],
      contact: {},
      enquiryCallToAction,
      layout: createDefaultLayout(enquiryCallToAction)
    }
  };
}

export function validateMicrositeEditor(value: MicrositeEditorValue, options: { aiEnabled?: boolean } = {}): Record<string, string> {
  const errors: Record<string, string> = {};
  const config = value.configuration;
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value.slug) || value.slug.length < 3 || value.slug.length > 80) {
    errors.slug = 'Use 3–80 lowercase letters, numbers, and single hyphens.';
  }
  if (value.canonicalHost && !/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(value.canonicalHost)) {
    errors.canonicalHost = 'Enter a hostname such as www.example.com, without a path.';
  }
  if (!config.headline.trim()) errors.headline = 'Headline is required.';
  if (!config.description.trim()) errors.description = 'Approved description is required.';
  try {
    const url = new URL(config.publicWebsiteUrl);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error();
  } catch {
    errors.publicWebsiteUrl = 'Enter a valid public website URL.';
  }
  for (const key of ['primaryColor', 'backgroundColor', 'foregroundColor', 'accentColor', 'surfaceColor', 'mutedColor'] as const) {
    const colour = config.brand[key];
    if (colour !== undefined && !/^#[0-9a-f]{6}$/i.test(colour)) errors[key] = 'Use a six-digit hex colour.';
  }
  if (!errors.backgroundColor && !errors.foregroundColor
    && getMicrositeContrastRatio(config.brand.backgroundColor, config.brand.foregroundColor) < 4.5) {
    errors.foregroundColor = 'Choose text and background colours with readable contrast.';
  }
  for (const [key, colour] of [['surfaceColor', config.brand.surfaceColor], ['mutedColor', config.brand.mutedColor]] as const) {
    if (colour && !errors[key] && !errors.foregroundColor
      && getMicrositeContrastRatio(colour, config.brand.foregroundColor) < 4.5) {
      errors[key] = 'Choose a colour with readable contrast against the text colour.';
    }
  }
  if (!config.startingInstruction.trim()) errors.startingInstruction = 'Starting instruction is required.';
  if (config.promptChoices.length < 1 || config.promptChoices.length > 12) {
    errors.promptChoices = 'Add between 1 and 12 visitor choices.';
  } else if (config.promptChoices.some((choice) => !choice.label.trim() || !choice.instruction.trim())) {
    errors.promptChoices = 'Every visitor choice needs a label and instruction.';
  } else if (new Set(config.promptChoices.map(({ id }) => id)).size !== config.promptChoices.length) {
    errors.promptChoices = 'Visitor choices must be unique.';
  }
  if (!config.enquiryCallToAction.heading.trim() || !config.enquiryCallToAction.label.trim()) {
    errors.enquiryCallToAction = 'Enquiry heading and button label are required.';
  }
  if (config.contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.contact.email)) {
    errors.contactEmail = 'Enter a valid public contact email.';
  }
  const layout = 'layout' in config && config.layout
    ? config.layout
    : createDefaultLayout(config.enquiryCallToAction);
  const components = layout.rows.flatMap((row) => row.columns).flatMap((column) => column.components);
  if (components.length === 0) errors.layout = 'Add at least one component to the layout.';
  for (const component of components) {
    if (component.type === 'clickToCall' && component.props.tapToDial && !config.contact.phone?.trim()) {
      errors[`component.${component.id}.phone`] = 'Add a public phone number or turn off tap-to-dial.';
    }
    if (component.type === 'richContent') {
      const visible = component.props.html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
      if (!visible && !component.props.embeds.length) errors[`component.${component.id}.content`] = 'Add formatted content or a trusted embed.';
      if (component.props.embeds.some((embed) => !normalizeMicrositeEmbedUrl(embed.provider, embed.url))) {
        errors[`component.${component.id}.embed`] = 'Use an approved YouTube, Vimeo, or Google Maps embed URL.';
      }
    }
    if (component.type === 'aiChat' && options.aiEnabled === false) {
      errors[`component.${component.id}.ai`] = 'Enable Project AI or remove the AI chat component.';
    }
  }
  return errors;
}

export function sourceStatusLabels(source: MicrositeSource): string[] {
  const labels: string[] = [];
  if (source.lastCompletedAt) labels.push('Fetched');
  if (source.latestVersion && source.latestVersion !== source.approvedVersion) labels.push('Awaiting review');
  if (source.approvedVersion) labels.push('Approved');
  if (source.status === 'failed') labels.push('Failed');
  if (source.status === 'failed' && source.approvedVersion) labels.push('Stale');
  if (source.status === 'refreshing') labels.push('Refreshing');
  if (labels.length === 0) labels.push('Not fetched');
  return labels;
}

export function isSameMicrositeEditorValue(left: MicrositeEditorValue, right: MicrositeEditorValue) {
  return JSON.stringify(left) === JSON.stringify(right);
}
